import React, { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { Html, Line } from '@react-three/drei';
import * as THREE from 'three';

// WhiteGreenDot component
const WhiteGreenDot = ({ size = "18px", dotColor = "#00ff00" }) => {
  return (
    <div 
      style={{
        display: 'block',
        position: 'relative',
        width: size,
        height: size,
        backgroundColor: 'white',
        borderRadius: '50%'
      }}
    >
      <div 
        style={{
          content: '""',
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: '66%',
          height: '66%',
          backgroundColor: dotColor,
          borderRadius: '50%',
          transform: 'translate(-50%, -50%)'
        }}
      />
    </div>
  );
};

// ModelText component
const ModelText = ({ text, variant = "right", dotColor = "#00ff00", dotSize = "12px" }) => {
  return (
    <div style={{
      display: 'flex',
      flexDirection: variant === 'right' ? 'row' : 'row-reverse',
      minWidth: '20vw',
      alignItems: 'center'
    }}>
      <div 
        style={{
          borderRadius: '50%',
          minWidth: dotSize,
          minHeight: dotSize,
          backgroundColor: dotColor,
          margin: 'auto 0',
          flexShrink: 0
        }}
      />
      <div 
        className="model-text"
        style={{
          position: 'relative',
          display: 'flex',
          fontSize: '18px',
          fontWeight: '600',
          color: '#425a70',
          overflowWrap: 'normal',
          zIndex: 1,
          paddingLeft: variant !== 'left' ? '10px' : '0',
          paddingRight: variant !== 'right' ? '10px' : '0',
          whiteSpace: 'nowrap'
        }}
      >
        {text}
      </div>
    </div>
  );
};

// Simple CSG-like subtraction using geometry manipulation
const createTubeGeometry = (outerRadius, innerRadius, height, segments) => {
  const geometry = new THREE.RingGeometry(innerRadius, outerRadius, 0, Math.PI * 2, segments);
  
  // Create tube by extruding the ring
  const shape = new THREE.Shape();
  shape.absarc(0, 0, outerRadius, 0, Math.PI * 2, false);
  const hole = new THREE.Shape();
  hole.absarc(0, 0, innerRadius, 0, Math.PI * 2, true);
  shape.holes.push(hole);
  
  const extrudeGeometry = new THREE.ExtrudeGeometry(shape, {
    depth: height,
    bevelEnabled: false,
    steps: 1
  });
  
  // Center the geometry
  extrudeGeometry.translate(0, 0, -height / 2);
  extrudeGeometry.rotateX(Math.PI / 2);
  
  return extrudeGeometry;
};

export const Tube = ({
  core,
  coreModification,
  coreColor,
  surfaceFunctionalization,
  surfaceFunctionalizationColor,
  lineColor = "#00ff00",
  baseRotation = [0, Math.PI / 4, Math.PI / 2],
  isNanoformImageGenerated = false
}) => {
  const modelGroup = useRef();
  const y = baseRotation[1];

  useFrame(() => {
    if (modelGroup.current) {
      if (isNanoformImageGenerated) {
        modelGroup.current.rotation.y = y;
      } else {
        modelGroup.current.rotation.y += 0.005;
      }
    }
  });

  // Create tube geometries (hollow cylinders)
  const coreGeometry = useMemo(() => createTubeGeometry(0.4, 0.3, 2.9, 30), []);
  const haloGeometry = useMemo(() => createTubeGeometry(0.5, 0.25, 2.89, 30), []);

  return (
    <group position={[0, -0.7, 0]}>
      {/* Core label */}
      {Boolean(core) && (
        <group>
          <Html className="core" position={[1, 1, 2]} zIndexRange={[0, 1]}>
            <ModelText
              dotSize="12px"
              text={coreModification ? `${core?.name}/${coreModification}` : (core?.name || 'Core')}
              variant="right"
              dotColor={lineColor}
            />
          </Html>
          <Html className="center" position={[0, 0.38, 2]} zIndexRange={[0, 1]}>
            <WhiteGreenDot size="18px" dotColor={lineColor} />
          </Html>
          <Line
            points={[
              [0, 0.37, 2],
              [0.7, 1, 2],
              [1, 1, 2],
            ]}
            lineWidth={3}
            color={lineColor}
          />
        </group>
      )}

      {/* Surface functionalization label */}
      {Boolean(surfaceFunctionalization) && (
        <group>
          <Html className="core--left" position={[-0.8, 0.85, 2]} zIndexRange={[0, 1]}>
            <ModelText 
              dotSize="12px" 
              text={surfaceFunctionalization} 
              variant="left" 
              dotColor={lineColor} 
            />
          </Html>
          <Html className="center" position={[-0.05, 0.58, 2]} zIndexRange={[0, 1]}>
            <WhiteGreenDot size="18px" dotColor={lineColor} />
          </Html>
          <Line
            points={[
              [-0.05, 0.58, 2],
              [-0.5, 0.85, 2],
              [-0.8, 0.85, 2],
            ]}
            lineWidth={1.5}
            color={lineColor}
          />
        </group>
      )}

      {/* 3D Model */}
      <group ref={modelGroup} rotation={baseRotation}>
        {/* Core tube */}
        <mesh geometry={coreGeometry}>
          <meshStandardMaterial 
            attach="material" 
            roughness={0} 
            metalness={0.1} 
            color={coreColor} 
          />
        </mesh>

        {/* Halo tube */}
        {Boolean(surfaceFunctionalization) && (
          <mesh geometry={haloGeometry}>
            <meshStandardMaterial 
              transparent 
              opacity={0.35} 
              color={surfaceFunctionalizationColor} 
            />
          </mesh>
        )}
      </group>
    </group>
  );
};